import { memo, useContext, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { Alert, Text, TextButton } from '@zillow/constellation-10';

import { ModularListingContext } from '../../containers/CreateListingPage/context';

import { useSelector } from '../../selectors/useSelector';

import actions from '../../containers/CreateListingPage/actions';

import { useGetisTopOfFunnelEnrollmentEligible } from '../../hooks/queries/freemium';

import accessUtils from '../../utils/accessUtils';
import numberUtils from '../../utils/numberUtils';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../constants/FreemiumConstants';

const PhoneNumberDisplayInfo = () => {
  const { reducerDispatch } = useContext(ModularListingContext);

  const {
    properties: {
      currentProperty: {
        freemium: {
          activeProduct: { type: activeProductType },
          isEnabled: isFreemiumEnabled,
        },
        trackingPhoneNumber,
      },
    },
  } = useSelector((state) => state, shallowEqual);

  const isActiveProductTypePremium = activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;
  const { data: isTopOfFunnelEnrollmentEligible } = useGetisTopOfFunnelEnrollmentEligible({
    enabled: !isActiveProductTypePremium,
  });

  const canAccessTopOfFunnelPremium = useMemo(
    () =>
      accessUtils.canAccessTopOfFunnelPremium({
        isFreemiumEnabled,
        isTopOfFunnelEnrollmentEligible,
        isActiveProductTypePremium,
      }),
    [isTopOfFunnelEnrollmentEligible, isFreemiumEnabled, isActiveProductTypePremium],
  );

  const handleClick = () => {
    actions.updateIsPremiumPurchaseModalOpen({
      reducerDispatch,
      isPremiumPurchaseModalOpen: true,
    });
  };

  const content = useMemo(() => {
    const showPremiumUpsell = canAccessTopOfFunnelPremium && !isActiveProductTypePremium;

    if (trackingPhoneNumber) {
      return `The phone number displayed to renters is ${numberUtils.formatPhoneNumberInput(trackingPhoneNumber)}.
      This allows us to better protect your identity and reduce spam. `;
    } else if (showPremiumUpsell) {
      return (
        <Text>
          This phone number will be displayed to renters. Want to prevent unwanted spam by using an alternate phone
          number?{' '}
          <TextButton textStyle="body-bold" onClick={handleClick}>
            Upgrade
          </TextButton>{' '}
          to premium.
        </Text>
      );
    }
    return `The phone number displayed to renters may differ from the one you provide.`;
  }, [trackingPhoneNumber]);

  return (
    <Alert
      data-testid="PhoneNumberDisplayInfoContainer"
      className="fs-mask"
      tone="info"
      actionButton={
        <TextButton aria-labelledby="learn-more-about-different-phone-number-alert" asChild>
          <a
            target="_blank"
            href="https://help.zillowrentalmanager.com/hc/en-us/articles/360047225453-Why-is-a-different-phone-number-showing-on-my-rental-listing-"
            rel="noreferrer"
          >
            Learn More
          </a>
        </TextButton>
      }
    >
      {content}
    </Alert>
  );
};

export default memo(PhoneNumberDisplayInfo);
